import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, XCircle, ExternalLink } from "lucide-react";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { KycStatusBadge } from "@/components/KycForm";

export const KycTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("submitted");

  useEffect(() => { api.get("/admin/kyc").then((r) => setRows(r.data)).catch(() => {}); }, []);

  const review = async (row, status) => {
    const note = status === "rejected" ? window.prompt("Reason shown to the investor:", "Document unclear — please re-upload") : "";
    if (status === "rejected" && note === null) return;
    setBusy(row.email);
    try {
      const res = await api.patch(`/admin/kyc/${encodeURIComponent(row.email)}/status`, { status, note });
      setRows((prev) => prev.map((r) => (r.email === row.email ? { ...r, ...res.data } : r)));
      toast.success(`${row.name || row.email} marked ${status}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };

  const shown = rows.filter((r) => filter === "all" || r.status === filter);

  return (
    <div data-testid="admin-kyc-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">KYC Review</h2>
          <p className="mt-1 text-sm text-slate-500">Verify PAN, ID and bank details before completing an investor's first off-market transfer.</p>
        </div>
        <div className="flex gap-px border border-slate-200 bg-slate-200">
          {["submitted", "verified", "rejected", "all"].map((f) => (
            <button key={f} data-testid={`admin-kyc-filter-${f}`} onClick={() => setFilter(f)} className={`px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] ${filter === f ? "bg-[#6D28D9] text-[#FFFFFF]" : "bg-[#F6F4FD] text-slate-500 hover:text-[#6D28D9]"}`}>
              {f} ({f === "all" ? rows.length : rows.filter((r) => r.status === f).length})
            </button>
          ))}
        </div>
      </div>

      <div data-testid="admin-kyc-list" className="mt-8 space-y-4">
        {shown.map((r) => (
          <div key={r.email} data-testid={`admin-kyc-row-${r.email}`} className="grid gap-5 border border-slate-200 bg-[#F6F4FD]/70 p-6 lg:grid-cols-[1fr_1fr_auto]">
            <div>
              <div className="flex flex-wrap items-center gap-3"><p className="font-display text-lg font-medium text-slate-900">{r.name || "Investor"}</p><KycStatusBadge status={r.status} /></div>
              <p className="mt-1 font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500">{r.email} · {r.submitted_at ? new Date(r.submitted_at).toLocaleString("en-IN") : ""}</p>
              <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 font-mono2 text-xs text-slate-700">
                <dt className="text-slate-400">PAN</dt><dd data-testid={`admin-kyc-pan-${r.email}`}>{r.pan_full}</dd>
                <dt className="text-slate-400">{r.id_type}</dt><dd>{r.id_number_full}</dd>
                <dt className="text-slate-400">Demat</dt><dd data-testid={`admin-kyc-demat-${r.email}`}>{r.demat_id_full || "—"}</dd>
                {r.note && <><dt className="text-slate-400">Note</dt><dd className="text-rose-700">{r.note}</dd></>}
              </dl>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <a href={fileUrl(r.id_doc_path)} target="_blank" rel="noreferrer" data-testid={`admin-kyc-doc-${r.email}`} className="group relative block h-40 overflow-hidden border border-slate-200">
                <img src={fileUrl(r.id_doc_path)} alt="ID document" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute bottom-2 right-2 flex items-center gap-1 bg-[#FFFFFF]/80 px-2 py-1 font-mono2 text-[9px] uppercase tracking-[0.15em] text-[#6D28D9]"><ExternalLink className="h-3 w-3" /> {r.id_type}</span>
              </a>
              {r.pan_doc_path ? (
                <a href={fileUrl(r.pan_doc_path)} target="_blank" rel="noreferrer" data-testid={`admin-kyc-pan-doc-${r.email}`} className="group relative block h-40 overflow-hidden border border-slate-200">
                  <img src={fileUrl(r.pan_doc_path)} alt="PAN card" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute bottom-2 right-2 flex items-center gap-1 bg-[#FFFFFF]/80 px-2 py-1 font-mono2 text-[9px] uppercase tracking-[0.15em] text-[#6D28D9]"><ExternalLink className="h-3 w-3" /> PAN</span>
                </a>
              ) : <div className="flex h-40 items-center justify-center border border-dashed border-slate-300 font-mono2 text-[9px] uppercase tracking-[0.15em] text-slate-400">No PAN card</div>}
            </div>
            <div className="flex flex-row gap-2 lg:flex-col">
              <button data-testid={`admin-kyc-verify-${r.email}`} onClick={() => review(r, "verified")} disabled={busy === r.email || r.status === "verified"} className="flex items-center justify-center gap-2 bg-emerald-600 px-4 py-2.5 font-mono2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#FFFFFF] transition-colors hover:bg-emerald-700 disabled:opacity-30"><ShieldCheck className="h-3.5 w-3.5" /> Verify</button>
              <button data-testid={`admin-kyc-reject-${r.email}`} onClick={() => review(r, "rejected")} disabled={busy === r.email || r.status === "rejected"} className="flex items-center justify-center gap-2 border border-rose-600/40 px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-[0.18em] text-rose-700 transition-colors hover:bg-rose-600/10 disabled:opacity-30"><XCircle className="h-3.5 w-3.5" /> Reject</button>
            </div>
          </div>
        ))}
        {shown.length === 0 && <p data-testid="admin-kyc-empty" className="border border-dashed border-slate-300 py-14 text-center font-mono2 text-xs uppercase tracking-[0.25em] text-slate-500">No {filter === "all" ? "" : filter} KYC records</p>}
      </div>
    </div>
  );
};
